<script>
import Spinner from 'shared/components/Spinner.vue';
import StagedFileCard from './StagedFileCard.vue';

// Phases come from onboardingKnowledge's `processingPhase` getter. The modal
// is deliberately presentational: it renders what it is given and emits one
// event per action, so the step owns the store calls and navigation.
export const PHASES = {
  WORKING: 'working',
  SUCCESS: 'success',
  PARTIAL: 'partial',
  TIMED_OUT: 'timedOut',
  ALL_FAILED: 'allFailed',
};

const PHASE_KEYS = {
  working: 'WORKING',
  success: 'SUCCESS',
  partial: 'PARTIAL',
  timedOut: 'TIMED_OUT',
  allFailed: 'ALL_FAILED',
};

export default {
  name: 'KnowledgeProcessingModal',
  components: { Spinner, StagedFileCard },
  props: {
    phase: { type: String, default: 'idle' },
    documents: { type: Array, default: () => [] },
    readyCount: { type: Number, default: 0 },
    totalCount: { type: Number, default: 0 },
    // 0-1, a weighted estimate (see overallProgress getter) — not measured.
    progress: { type: Number, default: 0 },
    // Whether the original File objects are still around to re-upload.
    // False after a refresh, when only the server's view of the dataset is left.
    canRetry: { type: Boolean, default: false },
  },
  emits: [
    'continue',
    'continuePartial',
    'retryFailed',
    'keepWaiting',
    'startOver',
    'tryAgain',
    'chooseDifferent',
  ],
  computed: {
    isOpen() {
      return this.phase !== 'idle';
    },
    phaseKey() {
      return PHASE_KEYS[this.phase] || 'WORKING';
    },
    percent() {
      return Math.round(Math.min(Math.max(this.progress, 0), 1) * 100);
    },
    hasFailed() {
      return this.documents.some(doc => doc.status === 'failed');
    },
    showProgress() {
      return this.phase === PHASES.WORKING;
    },
    // Files the user chose that are not in the knowledge base. They must never
    // just disappear, so they get their own notice (and a card badge).
    droppedDocs() {
      return this.documents.filter(doc => doc.status === 'dropped');
    },
  },
  methods: {
    noop() {},
    droppedReasonKey(reason) {
      return (
        { duplicate: 'DUPLICATE', not_uploaded: 'NOT_UPLOADED' }[reason] ||
        'REMOVED'
      );
    },
    badgeFor(doc) {
      const status = doc.status || 'failed';
      const base = 'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.STATUS';
      if (status === 'uploading') {
        const pct = Math.round((doc.uploadProgress || 0) * 100);
        return {
          label: `${this.$t(`${base}.UPLOADING`)} ${pct}%`,
          tone: 'text-slate-500 dark:text-slate-400',
          busy: true,
        };
      }
      if (status === 'indexing') {
        return {
          label: this.$t(`${base}.INDEXING`),
          tone: 'text-slate-500 dark:text-slate-400',
          busy: true,
        };
      }
      if (status === 'ready') {
        return {
          label: this.$t(`${base}.READY`),
          icon: 'checkmark',
          tone: 'text-green-600 dark:text-green-400',
        };
      }
      if (status === 'dropped') {
        return {
          label: this.$t(`${base}.DROPPED`),
          icon: 'warning',
          tone: 'text-amber-600 dark:text-amber-400',
        };
      }
      if (status === 'timed_out') {
        return {
          label: this.$t(`${base}.TIMED_OUT`),
          icon: 'clock',
          tone: 'text-amber-600 dark:text-amber-400',
        };
      }
      return {
        label: doc.error || this.$t(`${base}.FAILED`),
        icon: 'dismiss',
        tone: 'text-red-500',
      };
    },
  },
};
</script>

<template>
  <woot-modal
    :show="isOpen"
    :on-close="noop"
    :show-close-button="false"
    :close-on-escape="false"
    :close-on-backdrop-click="false"
  >
    <div
      class="flex flex-col gap-4 p-6 max-h-[85vh] overflow-auto"
      data-testid="kb_processing_modal"
      :data-phase="phase"
    >
      <h3
        class="text-lg font-medium text-slate-900 dark:text-slate-100"
        data-testid="kb_processing_title"
      >
        {{
          $t(
            `ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.TITLE.${phaseKey}`
          )
        }}
      </h3>

      <p
        v-if="!showProgress"
        class="text-sm text-slate-600 dark:text-slate-300"
        data-testid="kb_processing_body"
      >
        {{
          $t(
            `ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.BODY.${phaseKey}`,
            { ready: readyCount, total: totalCount }
          )
        }}
      </p>

      <div
        v-if="!showProgress && droppedDocs.length"
        class="flex flex-col gap-2 p-3 text-sm border rounded-lg border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
        role="status"
        data-testid="kb_processing_dropped_notice"
      >
        <span class="font-medium">
          {{
            droppedDocs.length === 1
              ? $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.DROPPED.TITLE_ONE'
                )
              : $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.DROPPED.TITLE_MANY',
                  { count: droppedDocs.length }
                )
          }}
        </span>
        <ul class="flex flex-col gap-1 pl-4 list-disc">
          <li
            v-for="doc in droppedDocs"
            :key="doc.id"
            :data-testid="`kb_processing_dropped_${doc.reason}`"
          >
            <span class="font-medium">{{ doc.name }}</span> —
            {{
              $t(
                `ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.DROPPED.${droppedReasonKey(
                  doc.reason
                )}`
              )
            }}
          </li>
        </ul>
      </div>

      <div v-if="showProgress" class="flex flex-col gap-2">
        <div
          class="w-full h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="percent"
          data-testid="kb_processing_progress"
        >
          <div
            class="h-full transition-all duration-500 rounded-full bg-woot-500"
            :style="{ width: `${percent}%` }"
          />
        </div>
        <div
          class="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400"
        >
          <span v-if="totalCount > 0" data-testid="kb_processing_count">
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.PROGRESS',
                { ready: readyCount, total: totalCount }
              )
            }}
          </span>
          <span class="ml-auto">{{ percent }}%</span>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.HINT') }}
        </p>
      </div>

      <div
        class="grid grid-cols-2 gap-3 sm:grid-cols-3"
        data-testid="kb_processing_files"
      >
        <StagedFileCard
          v-for="doc in documents"
          :key="doc.id"
          :file="{ name: doc.name, size: doc.size }"
          :removable="false"
        >
          <span
            class="flex justify-center gap-1 text-xs font-medium"
            :class="[
              badgeFor(doc).tone,
              // The default spinner is a wide, padded ring: stacked above the
              // label it stays centred instead of pushing the text sideways.
              badgeFor(doc).busy ? 'flex-col items-center' : 'items-center',
            ]"
            :data-testid="`kb_processing_badge_${doc.status}`"
          >
            <!-- primary: the default ring is white, invisible on this white card -->
            <Spinner
              v-if="badgeFor(doc).busy"
              size="small"
              color-scheme="primary"
            />
            <fluent-icon
              v-else-if="badgeFor(doc).icon"
              :icon="badgeFor(doc).icon"
              size="14"
            />
            {{ badgeFor(doc).label }}
          </span>
        </StagedFileCard>
      </div>

      <!-- Names the page by the sidebar's own label so the two stay in sync. -->
      <p
        class="text-xs text-slate-500 dark:text-slate-400"
        data-testid="kb_processing_later_hint"
      >
        {{
          $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.LATER_HINT', {
            page: $t('SIDEBAR.KNOWLEDGE'),
          })
        }}
      </p>

      <div v-if="!showProgress" class="flex flex-wrap justify-end gap-2">
        <woot-button
          v-if="phase === 'success'"
          data-testid="kb_processing_continue"
          @click="$emit('continue')"
        >
          {{
            $t(
              'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.CONTINUE'
            )
          }}
        </woot-button>

        <template v-if="phase === 'partial'">
          <woot-button
            v-if="canRetry && hasFailed"
            variant="smooth"
            data-testid="kb_processing_retry_failed"
            @click="$emit('retryFailed')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.RETRY_FAILED'
              )
            }}
          </woot-button>
          <woot-button
            v-if="documents.some(doc => doc.status === 'timed_out')"
            variant="smooth"
            data-testid="kb_processing_keep_waiting"
            @click="$emit('keepWaiting')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.KEEP_WAITING'
              )
            }}
          </woot-button>
          <woot-button
            data-testid="kb_processing_continue_partial"
            @click="$emit('continuePartial')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.CONTINUE_WITH',
                { count: readyCount }
              )
            }}
          </woot-button>
        </template>

        <template v-if="phase === 'timedOut'">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            data-testid="kb_processing_start_over"
            @click="$emit('startOver')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.START_OVER'
              )
            }}
          </woot-button>
          <woot-button
            data-testid="kb_processing_keep_waiting"
            @click="$emit('keepWaiting')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.KEEP_WAITING'
              )
            }}
          </woot-button>
        </template>

        <template v-if="phase === 'allFailed'">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            data-testid="kb_processing_choose_different"
            @click="$emit('chooseDifferent')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.CHOOSE_DIFFERENT'
              )
            }}
          </woot-button>
          <woot-button
            v-if="canRetry"
            data-testid="kb_processing_try_again"
            @click="$emit('tryAgain')"
          >
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.ACTIONS.TRY_AGAIN'
              )
            }}
          </woot-button>
        </template>
      </div>
    </div>
  </woot-modal>
</template>
